import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { appointmentStatus } from '../../../config/app.config';
import constant from '../../../constants/constants';
import { useAppointmentStatusesArchiveModal } from '../../modals/AppointmentStatusesArchive/AppointmentStatusesArchive.hooks';

const DEFAULT_PET_AVATAR = `${process.env.PUBLIC_URL}/img/default-animal-black.png`;

const EXPECTED_PHASE_ID = constant.appointmentPhases.expected.id;
const COMPLETED_PHASE_ID = constant.appointmentPhases.completed.id;

/**
 * Sets native title only when this element's text is truncated (ellipsis).
 * Measures the same node that carries PetCard ellipsis CSS.
 */
function OverflowTitle({
  as: Component = 'span',
  titleText,
  className,
  style,
  children,
  ...rest
}) {
  const measureRef = useRef(null);
  const [title, setTitle] = useState(undefined);

  const updateTitle = useCallback(() => {
    const el = measureRef.current;
    if (!el) {
      return;
    }

    const text =
      titleText == null || titleText === '' ? '' : String(titleText);
    const isTruncated = text !== '' && el.scrollWidth - el.clientWidth > 1;

    setTitle(isTruncated ? text : undefined);
  }, [titleText]);

  useLayoutEffect(() => {
    updateTitle();
    const raf = window.requestAnimationFrame(() => {
      updateTitle();
    });

    const el = measureRef.current;
    if (!el) {
      return () => window.cancelAnimationFrame(raf);
    }

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateTitle)
        : null;

    if (resizeObserver) {
      resizeObserver.observe(el);
    }

    window.addEventListener('resize', updateTitle);

    return () => {
      window.cancelAnimationFrame(raf);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateTitle);
    };
  }, [updateTitle, children]);

  return (
    <Component
      ref={measureRef}
      className={className}
      style={style}
      title={title}
      {...rest}
    >
      {children}
    </Component>
  );
}

function PetCardStethoscopeIcon() {
  return (
    <svg
      className="pet-card__doctor-icon"
      width="11"
      height="11"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4.99992 3.33464V3.33464C4.07944 3.33464 3.33325 4.08083 3.33325 5.0013V8.0013C3.33325 9.84225 4.82564 11.3346 6.66659 11.3346V11.3346M4.99992 3.33464C4.99992 3.70283 5.2984 4.0013 5.66659 4.0013C6.03478 4.0013 6.33325 3.70283 6.33325 3.33464C6.33325 2.96645 6.03478 2.66797 5.66659 2.66797C5.2984 2.66797 4.99992 2.96645 4.99992 3.33464ZM8.66659 3.33464C8.66659 3.70283 8.36811 4.0013 7.99992 4.0013C7.63173 4.0013 7.33325 3.70283 7.33325 3.33464C7.33325 2.96645 7.63173 2.66797 7.99992 2.66797C8.36811 2.66797 8.66659 2.96645 8.66659 3.33464ZM8.66659 3.33464V3.33464C9.40296 3.33464 9.99992 3.93159 9.99992 4.66797V8.0013C9.99992 9.84225 8.50753 11.3346 6.66659 11.3346V11.3346M6.66659 11.3346C6.66659 14.668 12.3333 15.0013 12.3333 11.3346V8.66797"
        stroke="#00B0EC"
        strokeWidth="1.5"
      />
      <path
        d="M13.3333 7.66797C13.3333 8.22025 12.8855 8.66797 12.3333 8.66797C11.781 8.66797 11.3333 8.22025 11.3333 7.66797C11.3333 7.11568 11.781 6.66797 12.3333 6.66797C12.8855 6.66797 13.3333 7.11568 13.3333 7.66797Z"
        stroke="#00B0EC"
      />
      <path
        d="M12.3333 7.66016H12.3399V7.66683H12.3333V7.66016Z"
        stroke="#00B0EC"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PetCard({
  color,
  firstName,
  lastName,
  parentName,
  petImage,
  doctorName = '',
  petDetails,
  appointmentType,
  phaseName,
  time,
  onCardClick,
  onOpenPhasePicker,
  id,
  status,
  metadata,
  laneId,
  mode = 'veterinary',
  createdFromTrackingBoard = false,
  highlightNewlyAdded = false,
}) {
  const isBoarding = mode === 'boarding';
  const displayParent = parentName || lastName || '';
  const avatarSrc = petImage || DEFAULT_PET_AVATAR;
  const phaseDisplayLabel = isBoarding
    ? appointmentType || phaseName
    : phaseName;
  const trimmedDoctorName = String(doctorName || '').trim();
  const showDoctor = !isBoarding && !!trimmedDoctorName;
  const showHistoryLink = appointmentStatus.ongoing === status;
  const showTime = !isBoarding && !!time && !createdFromTrackingBoard;
  const showPhaseChevrons = !isBoarding && !!onOpenPhasePicker;
  const currentPhaseId = +laneId;
  const { openAppointmentStatusesArchiveModal } =
    useAppointmentStatusesArchiveModal();

  const stopCardInteraction = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleHistoryClick = (event) => {
    stopCardInteraction(event);
    openAppointmentStatusesArchiveModal({ appointmentId: id });
  };

  const handleChevronClick = (event, direction) => {
    event.stopPropagation();
    event.preventDefault();

    if (!onOpenPhasePicker) {
      return;
    }

    onOpenPhasePicker({
      direction,
      anchorRect: event.currentTarget.getBoundingClientRect(),
      cardId: id,
      sourceId: laneId,
      cardDetails: {
        id,
        metadata,
        laneId,
        firstName,
        lastName,
        parentName,
        petImage,
        doctorName: trimmedDoctorName,
        petDetails,
        appointmentType,
        phaseName,
        time,
        status,
        color,
        createdFromTrackingBoard,
      },
    });
  };

  const historyLink = showHistoryLink ? (
    <button
      type="button"
      className="nav-link-style pet-card__history-link"
      onMouseDown={stopCardInteraction}
      onClick={handleHistoryClick}
    >
      Update history
    </button>
  ) : null;

  return (
    <div
      className={`card pet-card-container pet-card--${mode}${
        highlightNewlyAdded ? ' pet-card--newly-added' : ''
      }`}
      data-appointment-id={id}
      onClick={() => onCardClick(id, metadata, laneId)}
    >
      <div
        className="pet-card__phase-bar"
        style={{ backgroundColor: color }}
      />
      <div className="pet-card__body">
        <div className="pet-card__avatar-wrap">
          <img className="pet-card__avatar" src={avatarSrc} alt="" />
          {showPhaseChevrons ? (
            <div
              className="pet-card__phase-chevrons"
              onMouseDown={stopCardInteraction}
              onClick={stopCardInteraction}
            >
              <button
                type="button"
                className={`pet-card__phase-chevron pet-card__phase-chevron--previous${
                  currentPhaseId === EXPECTED_PHASE_ID ? ' is-disabled' : ''
                }`}
                aria-disabled={currentPhaseId === EXPECTED_PHASE_ID}
                aria-label="Previous phases"
                onMouseDown={stopCardInteraction}
                onClick={(event) => {
                  stopCardInteraction(event);
                  if (currentPhaseId === EXPECTED_PHASE_ID) return;
                  handleChevronClick(event, 'previous');
                }}
              >
                ‹
              </button>
              <button
                type="button"
                className={`pet-card__phase-chevron pet-card__phase-chevron--next${
                  currentPhaseId === COMPLETED_PHASE_ID ? ' is-disabled' : ''
                }`}
                aria-disabled={currentPhaseId === COMPLETED_PHASE_ID}
                aria-label="Next phases"
                onMouseDown={stopCardInteraction}
                onClick={(event) => {
                  stopCardInteraction(event);
                  if (currentPhaseId === COMPLETED_PHASE_ID) return;
                  handleChevronClick(event, 'next');
                }}
              >
                ›
              </button>
            </div>
          ) : null}
        </div>

        <div className="pet-card__content">
          <div className="pet-card__name-row">
            <OverflowTitle
              className="pet-card__pet-name"
              titleText={firstName}
            >
              {firstName}
            </OverflowTitle>
            {highlightNewlyAdded ? (
              <span className="pet-card__just-added-badge">Just added</span>
            ) : null}
          </div>

          <OverflowTitle
            as="div"
            className="pet-card__parent"
            titleText={displayParent}
          >
            {displayParent}
          </OverflowTitle>

          <OverflowTitle
            as="div"
            className="pet-card__details"
            titleText={petDetails}
          >
            {petDetails}
          </OverflowTitle>

          <div className="pet-card__type-row">
            {!isBoarding && appointmentType ? (
              <OverflowTitle
                className="pet-card__type-badge"
                titleText={appointmentType}
              >
                {appointmentType}
              </OverflowTitle>
            ) : null}
          </div>

          <div className="pet-card__phase-row">
            {phaseDisplayLabel ? (
              <OverflowTitle
                className="pet-card__phase-badge"
                style={{ color, borderColor: color }}
                titleText={phaseDisplayLabel}
              >
                {phaseDisplayLabel}
              </OverflowTitle>
            ) : null}

            {showTime ? (
              <span className="pet-card__time-block">
                <OverflowTitle
                  className="pet-card__time"
                  titleText={time}
                >
                  {time}
                </OverflowTitle>
              </span>
            ) : null}
          </div>

          <div
            className="pet-card__history-row"
            onMouseDown={stopCardInteraction}
          >
            {showDoctor ? (
              <div className="pet-card__doctor">
                <PetCardStethoscopeIcon />
                <OverflowTitle
                  className="pet-card__doctor-name"
                  titleText={trimmedDoctorName}
                >
                  {trimmedDoctorName}
                </OverflowTitle>
              </div>
            ) : null}
            {historyLink}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PetCard;
